import React, { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

import { motion, AnimatePresence } from "framer-motion";
import { format, isToday, isYesterday } from "date-fns";
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  TruckIcon, 
  PhoneIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  UserIcon,
  SpeakerWaveIcon,
  ArrowPathIcon,
  BanknotesIcon
} from "@heroicons/react/24/outline";
import LoadingPage from "./Loader";
import {
  fetchOrders,
  confirmOrder,
  markOrderAsDelivered,
  cancelOrder,
  OrderStatus
} from "../utils/restaurantApi";
import { showSuccessNotification, showErrorNotification } from './NotificationManager';
const RestaurantOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDelivering, setIsDelivering] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');

  // Fetch orders from API using new API utility
  const fetchOrdersData = useCallback(async (showToast = false) => {
    if (showToast) setIsRefreshing(true);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        showErrorNotification("Authentication token not found");
        return;
      }

      const decodedToken = jwtDecode(token);
      const restaurantCustomId = decodedToken.customId || decodedToken.restaurant?.customId || decodedToken.id;

      const ordersData = await fetchOrders(restaurantCustomId);
      setOrders(ordersData);
      
      if (showToast) {
        showSuccessNotification("Orders refreshed successfully!");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      showErrorNotification(error.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Confirm order using new API utility (simplified - no additional fees)
  const confirmOrderHandler = async (orderId) => {
    setIsConfirming(true);
    
    try {
      const response = await confirmOrder(orderId);

      // Update order in local state with new API response structure
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.customId === orderId 
            ? { 
                ...order, 
                status: response.order.status, 
                totalPrice: response.order.totalPrice || order.totalPrice, 
                fee: response.order.fee || order.fee,
                foodAmount: response.order.foodAmount || order.foodAmount || (response.order.totalPrice && response.order.fee ? response.order.totalPrice - response.order.fee : order.totalPrice)
              }
            : order
        )
      );

      showSuccessNotification("Order confirmed successfully!");
    } catch (error) {
      console.error("Error confirming order:", error);
      showErrorNotification(error.message || "Failed to confirm order");
    } finally {
      setIsConfirming(false);
    }
  };

  // Mark order as delivered using new API utility
  const markAsDeliveredHandler = async (orderId) => {
    setIsDelivering(true);
    
    try {
      await markOrderAsDelivered(orderId);

      // Update order in local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.customId === orderId 
            ? { ...order, status: OrderStatus.DELIVERED }
            : order
        )
      );

      showSuccessNotification("Order marked as delivered!");
    } catch (error) {
      console.error("Error marking order as delivered:", error);
      showErrorNotification(error.message || "Failed to mark order as delivered");
    } finally {
      setIsDelivering(false);
    }
  };

  // Cancel order
  const openCancelModal = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelOrderId(null);
    setCancelReason('');
  };

  const cancelOrderHandler = async () => {
    if (!cancelReason.trim()) {
      showErrorNotification("Please provide a reason for cancelling this order");
      return;
    }
    
    setIsCancelling(true);
    
    try {
      await cancelOrder(cancelOrderId, cancelReason.trim());

      // Update order in local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.customId === cancelOrderId 
            ? { ...order, status: 'cancelled' }
            : order
        )
      );

      showSuccessNotification("Order cancelled successfully!");
      closeCancelModal();
    } catch (error) {
      console.error("Error cancelling order:", error);
      showErrorNotification(error.message || "Failed to cancel order");
    } finally {
      setIsCancelling(false);
    }
  };

  // Format date display
  const formatOrderDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today at ${format(date, 'HH:mm')}`;
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, 'HH:mm')}`;
    } else {
      return format(date, 'MMM dd at HH:mm');
    }
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'Pending':
        return {
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: <ClockIcon className="w-4 h-4" />,
          text: 'Pending'
        };
      case 'Confirmed':
        return {
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: <CheckCircleIcon className="w-4 h-4" />,
          text: 'Confirmed'
        };
      case 'Delivered':
        return {
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: <TruckIcon className="w-4 h-4" />,
          text: 'Delivered'
        };
      case 'Canceled':
        return {
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: <XCircleIcon className="w-4 h-4" />,
          text: 'Canceled'
        };
      case 'Fee Requested':
        return {
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          icon: <CurrencyDollarIcon className="w-4 h-4" />,
          text: 'Fee Requested'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: <ClockIcon className="w-4 h-4" />,
          text: status || 'Unknown'
        };
    }
  };

  // Filter and sort orders based on status
  const filteredOrders = orders.filter(order => {
    if (orderFilter === 'all') return true;
    if (orderFilter === 'pending') return order.status === 'Pending';
    if (orderFilter === 'confirmed') return order.status === 'Confirmed';
    if (orderFilter === 'delivered') return order.status === 'Delivered';
    if (orderFilter === 'cancelled') return ['canceled', 'cancelled'].includes(order.status?.toLowerCase());
    return true;
  }).sort((a, b) => {
    // For pending orders, show oldest first (FIFO)
    // For all other statuses, show latest first
    if (orderFilter === 'pending') {
      return new Date(a.createdAt || a.orderDate) - new Date(b.createdAt || b.orderDate);
    }
    return new Date(b.createdAt || b.orderDate) - new Date(a.createdAt || a.orderDate);
  });

  // Get filter counts
  const getFilterCounts = () => {
    return {
      all: orders.length,
      pending: orders.filter(o => o.status === 'Pending').length,
      confirmed: orders.filter(o => o.status === 'Confirmed').length,
      delivered: orders.filter(o => o.status === 'Delivered').length,
      cancelled: orders.filter(o => ['canceled', 'cancelled'].includes(o.status?.toLowerCase())).length
    };
  };

  useEffect(() => {
    fetchOrdersData();
    
    // Set up auto-refresh every 30 seconds for new orders
    const interval = setInterval(() => {
      fetchOrdersData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchOrdersData]);

  if (loading) {
    return <LoadingPage />;
  }

  const filterCounts = getFilterCounts();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
          <p className="text-gray-600 mt-1">Manage and track all your restaurant orders</p>
        </div>
        
        <button
          onClick={() => fetchOrdersData(true)}
          disabled={isRefreshing}
          className="mt-4 md:mt-0 flex items-center gap-2 bg-cheese hover:bg-yellow-500 text-crust px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Orders'}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All Orders', count: filterCounts.all },
            { key: 'pending', label: 'Pending', count: filterCounts.pending },
            { key: 'confirmed', label: 'Confirmed', count: filterCounts.confirmed },
            { key: 'delivered', label: 'Delivered', count: filterCounts.delivered },
            { key: 'cancelled', label: 'Cancelled', count: filterCounts.cancelled }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setOrderFilter(filter.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                orderFilter === filter.key
                  ? 'bg-cheese text-crust'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
              <span className="bg-white bg-opacity-30 px-2 py-0.5 rounded-full text-xs">
                {filter.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-md text-center">
          <SpeakerWaveIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            {orderFilter === 'all' ? 'No orders yet' : `No ${orderFilter} orders`}
          </h3>
          <p className="text-gray-500">
            {orderFilter === 'all' 
              ? 'Orders will appear here when customers place them'
              : `Orders with ${orderFilter} status will appear here`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {filteredOrders.map((order) => {
              const statusDisplay = getStatusDisplay(order.status);
              const foodAmount = order.foodAmount || (order.totalPrice && order.fee ? (order.totalPrice - order.fee) : order.totalPrice || 0);
              
              return (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
                >
                  <div className="p-6">
                    {/* Order Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                      <div className="flex items-center gap-3 mb-2 md:mb-0">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-gray-900">
                              Order #{order.customId}
                            </h3>
                            {order.isGroupOrder && (
                              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                                Group Order
                              </span>
                            )}
                            {order.recipient && order.recipient.name && (
                              <span className="bg-pink-100 text-pink-800 text-xs font-medium px-2 py-1 rounded-full">
                                Gift Order
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatOrderDate(order.orderDate)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${statusDisplay.color}`}>
                          {statusDisplay.icon}
                          {statusDisplay.text}
                        </span>
                      </div>
                    </div>

                    {/* Customer Information */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <UserIcon className="w-4 h-4" />
                        Customer Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">
                            {order.recipient && order.recipient.name && order.user?.username ? 
                              `${order.recipient.name} (Gift from @${order.user.username})` : 
                              order.recipient && order.recipient.name ? 
                                order.recipient.name :
                                `@${order.user?.username || 'Unknown User'}`
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <PhoneIcon className="w-4 h-4 text-gray-500" />
                          <span>{order.phoneNumber || 'Not provided'}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-span-2">
                          <MapPinIcon className="w-4 h-4 text-gray-500 mt-0.5" />
                          <div>
                            <span>{order.location || 'Location not specified'}</span>
                            {order.nearestLandmark && (
                              <div className="text-gray-600 text-xs">
                                Landmark: {order.nearestLandmark}
                              </div>
                            )}
                          </div>
                        </div>
                        {order.note && (
                          <div className="md:col-span-2 mt-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                            <p className="text-sm text-yellow-800">
                              <strong>Note:</strong> {order.note}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Order Items</h4>
                      <div className="space-y-2">
                        {order.meals && order.meals.length > 0 ? (
                          order.meals.map((mealItem, index) => (
                            <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                              <div>
                                <span className="font-medium">{mealItem.meal?.name || 'Unknown Item'}</span>
                                <span className="text-gray-500 ml-2">x{mealItem.quantity || 1}</span>
                                {mealItem.meal?.description && (
                                  <div className="text-xs text-gray-500 mt-1">{mealItem.meal.description}</div>
                                )}
                              </div>
                              <span className="font-medium">
                                ₦{((mealItem.meal?.price || 0) * (mealItem.quantity || 1)).toLocaleString()}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="text-gray-500 text-sm py-2">
                            No items information available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Value */}
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <BanknotesIcon className="w-4 h-4" />
                        Order Value
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Food Amount:</span>
                          <span className="font-bold text-green-600">₦{foodAmount.toLocaleString()}</span>
                        </div>
                        {order.fee && (
                          <div className="flex justify-between">
                            <span>Delivery Fee:</span>
                            <span className="font-medium">₦{order.fee.toLocaleString()}</span>
                          </div>
                        )}
                        {order.totalPrice && (
                          <div className="flex justify-between border-t pt-2">
                            <span className="font-semibold">Total Amount:</span>
                            <span className="font-bold text-green-600">₦{order.totalPrice.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      {order.status === 'Pending' && (
                        <>
                          <button
                            onClick={() => confirmOrderHandler(order.customId)}
                            disabled={isConfirming}
                            className="flex-1 md:flex-none bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                            {isConfirming ? 'Processing...' : 'Confirm Order'}
                          </button>
                          <button
                            onClick={() => openCancelModal(order.customId)}
                            disabled={isCancelling}
                            className="flex-1 md:flex-none bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <XCircleIcon className="w-4 h-4" />
                            {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                          </button>
                        </>
                      )}
                      
                      {order.status === 'Confirmed' && (
                        <>
                          <button
                            onClick={() => markAsDeliveredHandler(order.customId)}
                            disabled={isDelivering}
                            className="flex-1 md:flex-none bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <TruckIcon className="w-4 h-4" />
                            {isDelivering ? 'Processing...' : 'Mark as Delivered'}
                          </button>
                          <button
                            onClick={() => openCancelModal(order.customId)}
                            disabled={isCancelling}
                            className="flex-1 md:flex-none bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <XCircleIcon className="w-4 h-4" />
                            {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                          </button>
                        </>
                      )}

                      {order.status === 'Delivered' && (
                        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-green-800">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">Order completed successfully!</span>
                          </div>
                        </div>
                      )}

                      {['canceled', 'cancelled'].includes(order.status?.toLowerCase()) && (
                        <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-red-800">
                            <XCircleIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">Order was cancelled</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <XCircleIcon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Cancel Order</h3>
                <p className="text-sm text-gray-600">Order #{cancelOrderId}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please provide a reason for cancelling this order:
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Out of stock, Kitchen issues, Customer request..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={3}
                maxLength={200}
              />
              <div className="text-xs text-gray-500 mt-1">
                {cancelReason.length}/200 characters
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeCancelModal}
                disabled={isCancelling}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Keep Order
              </button>
              <button
                onClick={cancelOrderHandler}
                disabled={isCancelling || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCancelling ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-4 h-4" />
                    Cancel Order
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RestaurantOrders;
